import { useMemo, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Tag,
  Select,
  Checkbox,
  Upload,
  Radio,
  Alert,
  Tooltip,
  Dropdown,
  App,
  type MenuProps,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  DeleteOutlined,
  MoreOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQueryClient } from '@tanstack/react-query';
import type { ChartOfAccountItem, ImportChartOfAccountsResult } from '@/types/accounting';
import {
  useAllChartOfAccounts,
  useCreateChartOfAccount,
  useUpdateChartOfAccount,
} from '@hooks/useAccounting';
import { accountingApi } from '@api/accountingApi';
import { useAuth } from '@stores/AuthContext';

const { Title, Text } = Typography;

const acqAccountTypeLabels: Record<string, { label: string; color: string }> = {
  active: { label: 'А', color: 'blue' },
  passive: { label: 'П', color: 'red' },
  active_passive: { label: 'АП', color: 'orange' },
};

interface ChartOfAccountTreeNode extends ChartOfAccountItem {
  children?: ChartOfAccountTreeNode[];
}

/**
 * Сортування top-level: спочатку балансові за кодом, потім позабалансові (IsOffBalance або code починається з '0').
 */
function acqOffBalanceFlag(acqRow: ChartOfAccountItem): boolean {
  return acqRow.isOffBalance || acqRow.code.startsWith('0');
}

function acqCompareNodes(a: ChartOfAccountItem, b: ChartOfAccountItem): number {
  const acqAOff = acqOffBalanceFlag(a) ? 1 : 0;
  const acqBOff = acqOffBalanceFlag(b) ? 1 : 0;
  if (acqAOff !== acqBOff) return acqAOff - acqBOff;
  return a.code.localeCompare(b.code, 'uk', { numeric: true });
}

/**
 * Будує дерево з flat-list через parentId. Top-level — ті, у кого parentId відсутній або
 * parent не існує у списку (на випадок битих даних). Сортує за `acqCompareNodes`.
 */
function acqBuildTree(acqRows: ChartOfAccountItem[]): ChartOfAccountTreeNode[] {
  const acqById = new Map<string, ChartOfAccountTreeNode>();
  acqRows.forEach((r) => acqById.set(r.id, { ...r }));

  const acqRoots: ChartOfAccountTreeNode[] = [];
  acqById.forEach((acqNode) => {
    if (acqNode.parentId && acqById.has(acqNode.parentId)) {
      const acqParent = acqById.get(acqNode.parentId)!;
      (acqParent.children ??= []).push(acqNode);
    } else {
      acqRoots.push(acqNode);
    }
  });

  const acqSortRec = (acqNodes: ChartOfAccountTreeNode[]) => {
    acqNodes.sort(acqCompareNodes);
    acqNodes.forEach((n) => n.children && acqSortRec(n.children));
  };
  acqSortRec(acqRoots);
  return acqRoots;
}

/**
 * Фільтрує дерево по пошуку: лишає вузли, що містять `q` у Code/Name, разом з усіма
 * предками. Якщо search порожній — повертає дерево як є.
 */
function acqFilterTree(acqTree: ChartOfAccountTreeNode[], acqQ: string): ChartOfAccountTreeNode[] {
  const acqQuery = acqQ.trim().toLowerCase();
  if (!acqQuery) return acqTree;

  const acqVisit = (acqNode: ChartOfAccountTreeNode): ChartOfAccountTreeNode | null => {
    const acqChildrenFiltered = (acqNode.children ?? [])
      .map(acqVisit)
      .filter((c): c is ChartOfAccountTreeNode => c !== null);
    const acqMatch =
      acqNode.code.toLowerCase().includes(acqQuery) ||
      acqNode.name.toLowerCase().includes(acqQuery);
    if (acqMatch || acqChildrenFiltered.length > 0) {
      return { ...acqNode, children: acqChildrenFiltered.length > 0 ? acqChildrenFiltered : undefined };
    }
    return null;
  };

  return acqTree.map(acqVisit).filter((n): n is ChartOfAccountTreeNode => n !== null);
}

export default function ChartOfAccountsPage() {
  const { user } = useAuth();
  const acqIsAdmin = user?.roles?.includes('Admin') ?? false;
  const acqQueryClient = useQueryClient();
  const { message: acqMessageApi } = App.useApp();

  const [acqSearch, acqSetSearch] = useState('');
  const [acqAccountTypeFilter, acqSetAccountTypeFilter] = useState<string | undefined>(undefined);
  const [acqShowOffBalance, acqSetShowOffBalance] = useState(true);
  const [acqModalOpen, acqSetModalOpen] = useState(false);
  const [acqEditing, acqSetEditing] = useState<ChartOfAccountItem | null>(null);
  const [acqForm] = Form.useForm();

  const [acqImportOpen, acqSetImportOpen] = useState(false);
  const [acqImportFile, acqSetImportFile] = useState<File | null>(null);
  const [acqImportMode, acqSetImportMode] = useState<'Append' | 'Replace' | 'FullReplace'>('Append');
  const [acqImportLoading, acqSetImportLoading] = useState(false);
  const [acqImportResult, acqSetImportResult] = useState<ImportChartOfAccountsResult | null>(null);

  const [acqDeleteOpen, acqSetDeleteOpen] = useState(false);
  const [acqDeleteLoading, acqSetDeleteLoading] = useState(false);

  const { data: acqAll, isLoading: acqLoading } = useAllChartOfAccounts();
  const acqCreateMutation = useCreateChartOfAccount();
  const acqUpdateMutation = useUpdateChartOfAccount();

  const acqFilteredFlat = useMemo(() => {
    let acqList = acqAll ?? [];
    if (acqAccountTypeFilter) acqList = acqList.filter((r) => r.accountType === acqAccountTypeFilter);
    if (!acqShowOffBalance) acqList = acqList.filter((r) => !acqOffBalanceFlag(r));
    return acqList;
  }, [acqAll, acqAccountTypeFilter, acqShowOffBalance]);

  const acqTree = useMemo(() => acqBuildTree(acqFilteredFlat), [acqFilteredFlat]);
  const acqVisibleTree = useMemo(() => acqFilterTree(acqTree, acqSearch), [acqTree, acqSearch]);

  const acqHandleAdd = () => {
    acqSetEditing(null);
    acqForm.resetFields();
    acqForm.setFieldsValue({ accountType: 'active', isGroup: false, isOffBalance: false });
    acqSetModalOpen(true);
  };

  const acqHandleAddChild = (acqParent: ChartOfAccountItem) => {
    acqSetEditing(null);
    acqForm.resetFields();
    acqForm.setFieldsValue({
      // Префікс коду — батьківський, користувач допише суфікс. Якщо батько вже "20",
      // підкаже, що child буде "20X" (201, 202, ...).
      code: acqParent.code,
      accountType: acqParent.accountType,
      isGroup: false,
      isCurrency: acqParent.isCurrency,
      isQuantitative: acqParent.isQuantitative,
      isTax: acqParent.isTax,
      isOffBalance: acqParent.isOffBalance,
      subconto1Type: acqParent.subconto1Type,
      subconto2Type: acqParent.subconto2Type,
      subconto3Type: acqParent.subconto3Type,
      parentId: acqParent.id,
    });
    acqSetModalOpen(true);
  };

  const acqHandleEdit = (acqRecord: ChartOfAccountItem) => {
    acqSetEditing(acqRecord);
    acqForm.setFieldsValue(acqRecord);
    acqSetModalOpen(true);
  };

  /**
   * При зміні parentId у формі — автоматично копіюємо успадковувані поля з батька
   * (тип, прапори, субконто). Code не міняємо — це робить тільки `acqHandleAddChild`,
   * щоб не затирати ввід користувача.
   */
  const acqOnParentChange = (acqNewParentId: string | undefined) => {
    if (!acqNewParentId || !acqAll) return;
    const acqP = acqAll.find((r) => r.id === acqNewParentId);
    if (!acqP) return;
    acqForm.setFieldsValue({
      accountType: acqP.accountType,
      isCurrency: acqP.isCurrency,
      isQuantitative: acqP.isQuantitative,
      isTax: acqP.isTax,
      isOffBalance: acqP.isOffBalance,
      subconto1Type: acqP.subconto1Type,
      subconto2Type: acqP.subconto2Type,
      subconto3Type: acqP.subconto3Type,
    });
  };

  const acqHandleSubmit = async () => {
    const acqValues = await acqForm.validateFields();
    if (acqEditing) {
      await acqUpdateMutation.mutateAsync({ id: acqEditing.id, ...acqValues });
    } else {
      await acqCreateMutation.mutateAsync(acqValues);
    }
    acqSetModalOpen(false);
    acqForm.resetFields();
    acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'chart-of-accounts'] });
  };

  const acqHandleDownloadTemplate = async () => {
    try {
      const acqBlob = await accountingApi.downloadChartOfAccountsTemplate();
      acqTriggerDownload(acqBlob, 'chart-of-accounts-template.xlsx');
    } catch {
      acqMessageApi.error('Не вдалося отримати шаблон');
    }
  };

  const acqHandleExport = async () => {
    try {
      const acqBlob = await accountingApi.exportChartOfAccounts();
      acqTriggerDownload(acqBlob, 'chart-of-accounts.xlsx');
    } catch {
      acqMessageApi.error('Не вдалося експортувати');
    }
  };

  const acqHandleImportSubmit = async () => {
    if (!acqImportFile) {
      acqMessageApi.warning('Оберіть файл');
      return;
    }
    acqSetImportLoading(true);
    try {
      const acqResult = await accountingApi.importChartOfAccounts(acqImportFile, acqImportMode);
      acqSetImportResult(acqResult);
      acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'chart-of-accounts'] });
      acqMessageApi.success(`Імпорт завершено: створено ${acqResult.created}, оновлено ${acqResult.updated}, пропущено ${acqResult.skipped}`);
    } catch (acqEx) {
      const acqMsg = (acqEx as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Помилка імпорту';
      acqMessageApi.error(acqMsg);
    } finally {
      acqSetImportLoading(false);
    }
  };

  const acqHandleDeleteAll = () => acqSetDeleteOpen(true);

  const acqConfirmDeleteAll = async () => {
    acqSetDeleteLoading(true);
    try {
      const acqResult = await accountingApi.deleteAllChartOfAccounts();
      acqMessageApi.success(`Видалено ${acqResult.deleted} рахунків`);
      acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'chart-of-accounts'] });
      acqSetDeleteOpen(false);
    } catch (acqEx) {
      const acqMsg = (acqEx as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Помилка очищення';
      acqMessageApi.error(acqMsg);
    } finally {
      acqSetDeleteLoading(false);
    }
  };

  const acqColumns: ColumnsType<ChartOfAccountTreeNode> = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (acqVal: string, acqRecord) => (
        <Space size={6}>
          <Text strong={acqRecord.isGroup}>{acqVal}</Text>
          {acqOffBalanceFlag(acqRecord) && (
            <Tooltip title="Позабалансовий">
              <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>off</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      render: (acqVal: string, acqRecord) => (
        <Text strong={acqRecord.isGroup}>{acqVal}</Text>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'accountType',
      key: 'accountType',
      width: 60,
      render: (acqVal: string) => {
        const acqInfo = acqAccountTypeLabels[acqVal];
        return acqInfo ? <Tag color={acqInfo.color}>{acqInfo.label}</Tag> : acqVal;
      },
    },
    {
      title: 'Валют.',
      dataIndex: 'isCurrency',
      key: 'isCurrency',
      width: 70,
      render: (v: boolean) => (v ? <Tag color="green">так</Tag> : null),
    },
    {
      title: 'Кільк.',
      dataIndex: 'isQuantitative',
      key: 'isQuantitative',
      width: 70,
      render: (v: boolean) => (v ? <Tag color="green">так</Tag> : null),
    },
    {
      title: 'Подат.',
      dataIndex: 'isTax',
      key: 'isTax',
      width: 70,
      render: (v: boolean) => (v ? <Tag color="green">так</Tag> : null),
    },
    {
      title: 'Субконто 1',
      dataIndex: 'subconto1Type',
      key: 'subconto1Type',
      width: 130,
    },
    {
      title: 'Субконто 2',
      dataIndex: 'subconto2Type',
      key: 'subconto2Type',
      width: 130,
    },
    {
      title: 'Субконто 3',
      dataIndex: 'subconto3Type',
      key: 'subconto3Type',
      width: 130,
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 90,
      render: (_: unknown, acqRecord) => (
        <Space size={2}>
          <Tooltip title="Редагувати">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => acqHandleEdit(acqRecord)} />
          </Tooltip>
          <Tooltip title="Додати підпорядкований">
            <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => acqHandleAddChild(acqRecord)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const acqMoreMenu: MenuProps = {
    items: [
      { key: 'template', icon: <FileExcelOutlined />, label: 'Завантажити шаблон (XLSX)' },
      { key: 'export', icon: <DownloadOutlined />, label: 'Експорт у XLSX' },
      ...(acqIsAdmin ? [
        { type: 'divider' as const },
        { key: 'import', icon: <UploadOutlined />, label: 'Імпорт з XLSX…' },
        { key: 'delete-all', icon: <DeleteOutlined />, label: 'Очистити план рахунків…', danger: true },
      ] : []),
    ],
    onClick: ({ key }) => {
      if (key === 'template') acqHandleDownloadTemplate();
      else if (key === 'export') acqHandleExport();
      else if (key === 'import') { acqSetImportResult(null); acqSetImportFile(null); acqSetImportOpen(true); }
      else if (key === 'delete-all') acqHandleDeleteAll();
    },
  };

  return (
    <>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space size={8}>
          <Title level={4} style={{ margin: 0 }}>План рахунків</Title>
          <Text type="secondary">{acqAll?.length ?? 0} рахунків</Text>
        </Space>
        <Space wrap>
          <Input
            placeholder="Пошук за кодом / назвою..."
            prefix={<SearchOutlined />}
            value={acqSearch}
            onChange={(e) => acqSetSearch(e.target.value)}
            style={{ width: 240 }}
            allowClear
          />
          <Select
            placeholder="Тип рахунку"
            value={acqAccountTypeFilter}
            onChange={acqSetAccountTypeFilter}
            allowClear
            style={{ width: 150 }}
            options={[
              { value: 'active', label: 'Активний' },
              { value: 'passive', label: 'Пасивний' },
              { value: 'active_passive', label: 'Активно-пасивний' },
            ]}
          />
          <Checkbox checked={acqShowOffBalance} onChange={(e) => acqSetShowOffBalance(e.target.checked)}>
            Позабалансові
          </Checkbox>
          <Button type="primary" icon={<PlusOutlined />} onClick={acqHandleAdd}>Додати</Button>
          <Dropdown menu={acqMoreMenu} placement="bottomRight">
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      </Space>

      <Table<ChartOfAccountTreeNode>
        columns={acqColumns}
        dataSource={acqVisibleTree}
        loading={acqLoading}
        rowKey="id"
        size="small"
        scroll={{ x: 1200 }}
        pagination={false}
        expandable={{
          defaultExpandAllRows: false,
          indentSize: 18,
        }}
      />

      {/* Створення / редагування */}
      <Modal
        title={acqEditing ? 'Редагувати рахунок' : 'Новий рахунок'}
        open={acqModalOpen}
        onOk={acqHandleSubmit}
        onCancel={() => acqSetModalOpen(false)}
        confirmLoading={acqCreateMutation.isPending || acqUpdateMutation.isPending}
        destroyOnHidden
        width={600}
      >
        <Form form={acqForm} layout="vertical">
          <Form.Item
            name="parentId"
            label="Підпорядкований до (батько)"
            extra="Якщо обрати — успадкує тип, прапори та субконто з батька. Залиш порожнім для top-level рахунку."
          >
            <Select
              showSearch
              allowClear
              placeholder="— top-level —"
              optionFilterProp="label"
              onChange={acqOnParentChange}
              options={(acqAll ?? []).map((r) => ({
                value: r.id,
                label: `${r.code} — ${r.name}`,
              }))}
            />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="code" label="Код" rules={[{ required: true, message: 'Введіть код' }]} style={{ width: 140 }}>
              <Input />
            </Form.Item>
            <Form.Item name="accountType" label="Тип" rules={[{ required: true }]} style={{ width: 200 }}>
              <Select
                options={[
                  { value: 'active', label: 'Активний' },
                  { value: 'passive', label: 'Пасивний' },
                  { value: 'active_passive', label: 'Активно-пасивний' },
                ]}
              />
            </Form.Item>
          </Space>
          <Form.Item name="name" label="Назва" rules={[{ required: true, message: 'Введіть назву' }]}>
            <Input />
          </Form.Item>
          <Space wrap>
            <Form.Item name="isGroup" valuePropName="checked" noStyle><Checkbox>Група</Checkbox></Form.Item>
            <Form.Item name="isCurrency" valuePropName="checked" noStyle><Checkbox>Валютний</Checkbox></Form.Item>
            <Form.Item name="isQuantitative" valuePropName="checked" noStyle><Checkbox>Кількісний</Checkbox></Form.Item>
            <Form.Item name="isTax" valuePropName="checked" noStyle><Checkbox>Податковий</Checkbox></Form.Item>
            <Form.Item name="isOffBalance" valuePropName="checked" noStyle><Checkbox>Позабалансовий</Checkbox></Form.Item>
          </Space>
          <Form.Item name="subconto1Type" label="Субконто 1" style={{ marginTop: 16 }}>
            <Input placeholder="Тип субконто (наприклад: Counterparty)" />
          </Form.Item>
          <Form.Item name="subconto2Type" label="Субконто 2">
            <Input placeholder="Тип субконто" />
          </Form.Item>
          <Form.Item name="subconto3Type" label="Субконто 3">
            <Input placeholder="Тип субконто" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Очистити план рахунків (state-driven, без App.useApp().confirm) */}
      <Modal
        title="Очистити план рахунків?"
        open={acqDeleteOpen}
        onOk={acqConfirmDeleteAll}
        onCancel={() => acqSetDeleteOpen(false)}
        confirmLoading={acqDeleteLoading}
        okText="Очистити"
        okButtonProps={{ danger: true }}
        cancelText="Скасувати"
      >
        <Space direction="vertical">
          <Text>
            Буде видалено <Text strong>{acqAll?.length ?? 0}</Text> рахунків (hard delete, без можливості відновлення).
          </Text>
          <Text type="secondary">Дія неможлива якщо існують проводки в журналі.</Text>
        </Space>
      </Modal>

      {/* Імпорт XLSX */}
      <Modal
        title="Імпорт плану рахунків з XLSX"
        open={acqImportOpen}
        onOk={acqHandleImportSubmit}
        onCancel={() => acqSetImportOpen(false)}
        confirmLoading={acqImportLoading}
        okText="Імпортувати"
        cancelText="Закрити"
        width={640}
        destroyOnHidden
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Структура файлу"
            description="Колонки: Code, Name, AccountType, IsGroup, IsCurrency, IsQuantitative, IsTax, IsOffBalance, Subconto1, Subconto2, Subconto3, ParentCode. Завантажте шаблон через ⋯ → Завантажити шаблон."
          />
          <div>
            <Text strong>Режим:</Text>
            <div style={{ marginTop: 4 }}>
              <Radio.Group value={acqImportMode} onChange={(e) => acqSetImportMode(e.target.value)}>
                <Space direction="vertical">
                  <Radio value="Append">Додати (пропускати наявні коди)</Radio>
                  <Radio value="Replace">Оновити (перезаписати наявні, не чіпати ті, яких немає у файлі)</Radio>
                  <Radio value="FullReplace"><Text type="danger">Замінити повністю</Text> — видалити поточні {acqAll?.length ?? 0} і залити нові з файлу</Radio>
                </Space>
              </Radio.Group>
            </div>
          </div>
          {acqImportMode === 'FullReplace' && (
            <Alert type="warning" showIcon
              message="Усі поточні рахунки будуть видалені (hard delete). Операція неможлива якщо є проводки в журналі." />
          )}
          <Upload
            beforeUpload={(acqFile) => { acqSetImportFile(acqFile); return false; }}
            maxCount={1}
            onRemove={() => acqSetImportFile(null)}
            accept=".xlsx"
          >
            <Button icon={<UploadOutlined />}>Обрати XLSX</Button>
          </Upload>
          {acqImportResult && (
            <Alert
              type={acqImportResult.errors.length > 0 ? 'warning' : 'success'}
              message={`Створено: ${acqImportResult.created}, оновлено: ${acqImportResult.updated}, пропущено: ${acqImportResult.skipped}`}
              description={acqImportResult.errors.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                  {acqImportResult.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                  {acqImportResult.errors.length > 20 && <li>… ще {acqImportResult.errors.length - 20}</li>}
                </ul>
              )}
            />
          )}
        </Space>
      </Modal>
    </>
  );
}

function acqTriggerDownload(acqBlob: Blob, acqFilename: string) {
  const acqUrl = URL.createObjectURL(acqBlob);
  const acqA = document.createElement('a');
  acqA.href = acqUrl;
  acqA.download = acqFilename;
  document.body.appendChild(acqA);
  acqA.click();
  document.body.removeChild(acqA);
  URL.revokeObjectURL(acqUrl);
}
